import React from 'react';
import { 
  Plus, Download, ArrowDownLeft, X, Loader2 
} from 'lucide-react';
import { updateDoc, doc, serverTimestamp } from '../firebase';
import { db, handleFirestoreError, OperationType } from '../firebase';
import { cn } from '../lib/utils';

const normalizeBudget = (budget: any): any => {
  const source = budget || {};
  const normalized = { ...source };
  const id = source.id ?? source.budgetId ?? source.budget_id ?? source._id;
  const name = source.name ?? source.budgetName ?? source.budget_name;
  const startDate = source.startDate ?? source.start_date;
  const period = source.period ?? source.periodMonths ?? source.period_months;
  const trackingSelections = source.trackingSelections ?? source.tracking_selections;
  const values = source.values ?? source.budgetValues ?? source.budget_values;

  if (id !== undefined) normalized.id = String(id);
  if (name !== undefined) normalized.name = name;
  if (startDate !== undefined) normalized.startDate = startDate;
  if (period !== undefined) normalized.period = Number(period);
  if (trackingSelections !== undefined) normalized.trackingSelections = trackingSelections;
  if (values !== undefined) normalized.values = values;

  return normalized;
};

const getBudgetList = (payload: any): any[] => {
  const candidates = [
    payload,
    payload?.budgets,
    payload?.data,
    payload?.data?.budgets,
    payload?.items,
  ];

  return (candidates.find(Array.isArray) || []).map(normalizeBudget);
};

const getBudgetFromResponse = (payload: any): any | null => {
  const budget = payload?.budget ?? payload?.data?.budget ?? payload?.data ?? payload;
  return budget && !Array.isArray(budget) ? normalizeBudget(budget) : null;
};

const readResponseBody = async (response: Response): Promise<any | null> => {
  const text = await response.text();
  if (!text) return null;

  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
};

interface BudgetManagerProps {
  accounts: any[];
  orgId?: string | null;
  actualsMap: Record<string, number>;
  showActuals: boolean;
  setShowActuals: (val: boolean) => void;
  selectedBudgetId: string | null;
  setSelectedBudgetId: (id: string) => void;
  budgets: any[];
  setIsBudgetModalOpen: (val: boolean) => void;
  isBudgetModalOpen: boolean;
  budgetStartDate: string;
  setBudgetStartDate: (date: string) => void;
  budgetPeriod: number;
  setBudgetPeriod: (period: number) => void;
  trackingCategories: any[];
  activeBudget: any;
  handleSaveBudget: () => void;
  isSavingBudget: boolean;
  budgetMonths: string[];
  budgetValues: Record<string, number>;
  setBudgetValues: React.Dispatch<React.SetStateAction<Record<string, number>>>;
  newBudgetName: string;
  setNewBudgetName: (name: string) => void;
  newBudgetTrackingSelections: Record<string, string>;
  setNewBudgetTrackingSelections: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  handleCreateBudget: () => void;
  setActiveReport: (report: string | null) => void;
}

export const BudgetManager = ({
  accounts, orgId, actualsMap, showActuals, setShowActuals,
  selectedBudgetId, setSelectedBudgetId, budgets,
  setIsBudgetModalOpen, isBudgetModalOpen, budgetStartDate, setBudgetStartDate,
  budgetPeriod, setBudgetPeriod, trackingCategories, activeBudget,
  handleSaveBudget, isSavingBudget, budgetMonths, budgetValues, setBudgetValues,
  newBudgetName, setNewBudgetName, newBudgetTrackingSelections, setNewBudgetTrackingSelections,
  handleCreateBudget, setActiveReport
}: BudgetManagerProps) => {
  const [localBudgets, setLocalBudgets] = React.useState<any[]>(() => budgets.map(normalizeBudget));
  const [hasLoadedLocalBudgets, setHasLoadedLocalBudgets] = React.useState(false);
  const [isLocalBudgetSaving, setIsLocalBudgetSaving] = React.useState(false);
  const appAuthToken = typeof window === 'undefined' ? null : localStorage.getItem('app_auth_token');
  const localApiOrgId = orgId
    ?? activeBudget?.organizationId
    ?? activeBudget?.organization_id
    ?? activeBudget?.orgId
    ?? activeBudget?.org_id
    ?? budgets[0]?.organizationId
    ?? budgets[0]?.organization_id
    ?? budgets[0]?.orgId
    ?? budgets[0]?.org_id
    ?? accounts[0]?.organizationId
    ?? accounts[0]?.organization_id
    ?? accounts[0]?.orgId
    ?? accounts[0]?.org_id;
  const localApiUrl = appAuthToken && localApiOrgId
    ? `/api/organizations/${localApiOrgId}/budgets`
    : null;

  React.useEffect(() => {
    setHasLoadedLocalBudgets(false);
  }, [localApiUrl]);

  React.useEffect(() => {
    if (!localApiUrl || !hasLoadedLocalBudgets) {
      setLocalBudgets(budgets.map(normalizeBudget));
    }
  }, [budgets, hasLoadedLocalBudgets, localApiUrl]);

  const syncBudgetState = React.useCallback((budget: any) => {
    if (budget.startDate !== undefined) setBudgetStartDate(budget.startDate);
    if (budget.period !== undefined) setBudgetPeriod(budget.period);
    if (budget.values !== undefined) setBudgetValues(budget.values || {});
  }, [setBudgetPeriod, setBudgetStartDate, setBudgetValues]);

  React.useEffect(() => {
    if (!localApiUrl || !appAuthToken) return;

    let isCurrent = true;

    const loadBudgets = async () => {
      try {
        const response = await fetch(localApiUrl, {
          headers: { Authorization: `Bearer ${appAuthToken}` },
        });
        const payload = await readResponseBody(response);

        if (!response.ok) {
          throw new Error(payload?.detail || 'Unable to load budgets');
        }

        const loadedBudgets = getBudgetList(payload);
        if (!isCurrent) return;

        setLocalBudgets(loadedBudgets);
        setHasLoadedLocalBudgets(true);
        const selectedBudget = loadedBudgets.find(budget => budget.id === selectedBudgetId) || loadedBudgets[0];
        if (selectedBudget) {
          if (selectedBudget.id !== selectedBudgetId) setSelectedBudgetId(selectedBudget.id);
          syncBudgetState(selectedBudget);
        }
      } catch (error) {
        // Firebase-provided budgets remain visible when the local API is unavailable.
        console.error('Unable to load local budgets:', error);
      }
    };

    void loadBudgets();

    return () => {
      isCurrent = false;
    };
  }, [appAuthToken, localApiUrl, selectedBudgetId, setSelectedBudgetId, syncBudgetState]);

  const currentBudget = localBudgets.find(budget => budget.id === selectedBudgetId) || activeBudget;
  const isBudgetSaving = isSavingBudget || isLocalBudgetSaving;

  const updateBudget = async (
    changes: Record<string, any>,
    firestoreChanges: Record<string, any> = changes,
  ) => {
    if (!selectedBudgetId) return;

    if (localApiUrl && appAuthToken) {
      setIsLocalBudgetSaving(true);
      try {
        const payload: Record<string, any> = { id: selectedBudgetId };
        if (changes.startDate !== undefined) payload.start_date = changes.startDate;
        if (changes.period !== undefined) payload.period = changes.period;
        if (changes.trackingSelections !== undefined) payload.tracking_selections = changes.trackingSelections;
        if (changes.values !== undefined) payload.values = changes.values;

        const response = await fetch(localApiUrl, {
          method: 'PATCH',
          headers: {
            Authorization: `Bearer ${appAuthToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });
        const responsePayload = await readResponseBody(response);

        if (!response.ok) {
          throw new Error(responsePayload?.detail || 'Unable to update budget');
        }

        const updatedBudget = normalizeBudget({
          ...currentBudget,
          ...changes,
          ...(getBudgetFromResponse(responsePayload) || {}),
          id: selectedBudgetId,
        });
        setLocalBudgets(previous => previous.map(budget => (
          budget.id === selectedBudgetId ? updatedBudget : budget
        )));
        syncBudgetState(updatedBudget);
      } catch (error) {
        console.error('Unable to update local budget:', error);
      } finally {
        setIsLocalBudgetSaving(false);
      }
      return;
    }

    try {
      await updateDoc(doc(db, 'budgets', selectedBudgetId), {
        ...firestoreChanges,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, 'budgets');
    }
  };

  const handleLocalBudgetSave = async () => {
    if (!localApiUrl || !appAuthToken || !selectedBudgetId) {
      handleSaveBudget();
      return;
    }

    await updateBudget({ values: budgetValues });
  };

  const handleLocalBudgetCreate = async () => {
    if (!localApiUrl || !appAuthToken) {
      handleCreateBudget();
      return;
    }

    setIsLocalBudgetSaving(true);
    try {
      const response = await fetch(localApiUrl, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${appAuthToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: newBudgetName.trim(),
          start_date: budgetStartDate,
          period: budgetPeriod,
          tracking_selections: newBudgetTrackingSelections,
          values: {},
        }),
      });
      const payload = await readResponseBody(response);

      if (!response.ok) {
        throw new Error(payload?.detail || 'Unable to create budget');
      }

      const createdBudget = normalizeBudget({
        name: newBudgetName.trim(),
        startDate: budgetStartDate,
        period: budgetPeriod,
        trackingSelections: newBudgetTrackingSelections,
        values: {},
        ...(getBudgetFromResponse(payload) || {}),
      });

      if (!createdBudget.id) {
        throw new Error('The local API did not return a budget ID');
      }

      setLocalBudgets(previous => [...previous, createdBudget]);
      setSelectedBudgetId(createdBudget.id);
      syncBudgetState(createdBudget);
      setNewBudgetName('');
      setNewBudgetTrackingSelections({});
      setIsBudgetModalOpen(false);
    } catch (error) {
      console.error('Unable to create local budget:', error);
    } finally {
      setIsLocalBudgetSaving(false);
    }
  };

  const selectBudget = (budgetId: string) => {
    setSelectedBudgetId(budgetId);
    const budget = localBudgets.find(item => item.id === budgetId);
    if (budget) syncBudgetState(budget);
  };

  const groupedAccounts = {
    'Revenue': accounts.filter(a => a.type === 'Sales' || a.type === 'Other Income' || a.category === 'Revenue'),
    'Cost of Sales': accounts.filter(a => a.type === 'Direct Cost'),
    'Operating Expenses': accounts.filter(a => a.type === 'Expense' || (a.category === 'Expense' && a.type !== 'Direct Cost')),
    'Assets': accounts.filter(a => ['Bank', 'Current Asset', 'Fixed Asset', 'Non-current Asset'].includes(a.type)),
    'Liabilities': accounts.filter(a => ['Current Liability', 'Non-current Liability'].includes(a.type)),
    'Equity': accounts.filter(a => a.type === 'Equity')
  };

  const getActualsForMonth = (accountCode: string, month: string) => {
    return Math.abs(actualsMap[`${accountCode}_${month}`] || 0);
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <button 
          onClick={() => setActiveReport(null)}
          className="flex items-center gap-2 text-sm font-bold hover:opacity-70 transition-opacity"
        >
          <ArrowDownLeft className="w-4 h-4 rotate-45" />
          <span>Back to Reports</span>
        </button>
        
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={() => setShowActuals(!showActuals)}
            className={cn(
              "px-4 py-2 rounded-full text-[10px] font-bold uppercase tracking-widest transition-all border-2",
              showActuals ? "bg-muted text-foreground border-border" : "bg-card text-foreground border-border"
            )}
          >
            {showActuals ? 'Hide Actuals' : 'Show Actuals'}
          </button>

          <div className="flex items-center gap-2 bg-white/50 border border-[#141414] px-4 py-2 rounded-full">
            <span className="text-[10px] font-bold uppercase tracking-widest opacity-40">Budget</span>
            <select 
              value={selectedBudgetId || ''}
              onChange={(e) => {
                if (e.target.value === 'new') setIsBudgetModalOpen(true);
                else selectBudget(e.target.value);
              }}
              className="bg-transparent border-none outline-none text-[10px] font-bold uppercase tracking-widest appearance-none cursor-pointer min-w-[120px]"
            >
              <option value="" disabled>Select Budget</option>
              {localBudgets.map(b => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
              <option value="new">+ Add new budget</option>
            </select>
          </div>

          <button
            onClick={() => setIsBudgetModalOpen(true)}
            className="p-2 bg-[#141414] text-[#E4E3E0] rounded-full hover:scale-110 transition-transform shadow-lg"
            title="Create New Budget"
          >
            <Plus className="w-4 h-4" />
          </button>

          <div className="flex items-center gap-2 bg-white/50 border border-[#141414] px-4 py-2 rounded-full">
            <span className="text-[10px] font-bold uppercase tracking-widest opacity-40">Start</span>
            <input 
              type="month" 
              value={budgetStartDate}
              onChange={async (e) => {
                const newDate = e.target.value;
                setBudgetStartDate(newDate);
                await updateBudget({ startDate: newDate });
              }}
              className="bg-transparent border-none outline-none text-[10px] font-bold uppercase tracking-widest"
            />
          </div>

          <div className="flex items-center gap-2 bg-white/50 border border-[#141414] px-4 py-2 rounded-full">
            <span className="text-[10px] font-bold uppercase tracking-widest opacity-40">Period</span>
            <select 
              value={budgetPeriod}
              onChange={async (e) => {
                const newPeriod = Number(e.target.value);
                setBudgetPeriod(newPeriod);
                await updateBudget({ period: newPeriod });
              }}
              className="bg-transparent border-none outline-none text-[10px] font-bold uppercase tracking-widest appearance-none cursor-pointer"
            >
              <option value={3}>3 Months</option>
              <option value={6}>6 Months</option>
              <option value={12}>12 Months</option>
              <option value={24}>24 Months</option>
            </select>
          </div>

          {trackingCategories.map(category => (
            <div key={category.id} className="flex items-center gap-2 bg-white/50 border border-[#141414] px-4 py-2 rounded-full">
              <span className="text-[10px] font-bold uppercase tracking-widest opacity-40">{category.name}</span>
              <select 
                value={currentBudget?.trackingSelections?.[category.id] || ''}
                disabled={!currentBudget}
                onChange={async (e) => {
                  if (!selectedBudgetId) return;
                  const trackingSelections = {
                    ...(currentBudget?.trackingSelections || {}),
                    [category.id]: e.target.value,
                  };
                  await updateBudget(
                    { trackingSelections },
                    { [`trackingSelections.${category.id}`]: e.target.value },
                  );
                }}
                className="bg-transparent border-none outline-none text-[10px] font-bold uppercase tracking-widest appearance-none cursor-pointer"
              >
                <option value="">All {category.name}</option>
                {category.options?.map((option: string) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
          ))}

          <button 
            onClick={handleLocalBudgetSave}
            disabled={isBudgetSaving}
            className="flex items-center gap-2 px-6 py-2 bg-[#141414] text-[#E4E3E0] rounded-full text-xs font-bold hover:opacity-90 transition-all disabled:opacity-50"
          >
            {isBudgetSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4 rotate-180" />}
            Save Budget
          </button>
        </div>
      </div>

      <div className="bg-card border border-border rounded-[40px] p-8 shadow-sm overflow-hidden text-foreground">
        <div className="mb-8 flex justify-between items-end">
          <div>
            <h1 className="text-3xl font-bold tracking-tighter italic serif">{currentBudget?.name || 'Overall Budget'}</h1>
            <p className="text-xs uppercase tracking-widest font-bold opacity-40 mt-1">Budget Manager</p>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b-2 border-[#141414]">
                <th className="p-4 text-left text-[10px] uppercase font-bold tracking-widest opacity-40 sticky left-0 bg-white/80 backdrop-blur-sm z-10 min-w-[200px]">Account</th>
                {budgetMonths.map((month, idx) => (
                  <th key={`header-${month}-${idx}`} className="p-4 text-right text-[10px] uppercase font-bold tracking-widest opacity-40 min-w-[120px]">
                    <div className="flex flex-col">
                      <span>{new Date(month + '-01').toLocaleDateString('en-GB', { month: 'short', year: '2-digit' })}</span>
                      {showActuals && (
                        <div className="flex justify-between mt-1 text-[8px] opacity-60">
                          <span>Budget</span>
                          <span>Actual</span>
                        </div>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[#141414]/5">
              {Object.entries(groupedAccounts).map(([category, categoryAccounts]) => (
                <React.Fragment key={category}>
                  <tr className="bg-[#141414]/5">
                    <td colSpan={showActuals ? budgetMonths.length * 2 + 1 : budgetMonths.length + 1} className="p-4 text-[10px] uppercase font-bold tracking-widest opacity-60">{category}</td>
                  </tr>
                  {categoryAccounts.map(account => (
                    <tr key={account.id} className="hover:bg-[#141414]/5 transition-colors group">
                      <td className="p-4 text-xs font-bold sticky left-0 bg-white/80 backdrop-blur-sm z-10 border-r border-[#141414]/5">
                        {account.name}
                        <span className="block text-[10px] opacity-30 font-mono">{account.code}</span>
                      </td>
                      {budgetMonths.map((month, idx) => {
                        const key = `${account.code}_${month}`;
                        const actual = showActuals ? getActualsForMonth(account.code, month) : 0;
                        return (
                          <td key={`${account.id}-${month}-${idx}`} className="p-2">
                            <div className={cn("flex flex-col gap-1", showActuals && "flex-row items-center")}>
                              <input 
                                type="number"
                                value={budgetValues[key] || ''}
                                onChange={(e) => setBudgetValues(prev => ({
                                  ...prev,
                                  [key]: Number(e.target.value)
                                }))}
                                className="w-full bg-transparent border-none outline-none text-right font-mono text-xs focus:bg-white focus:ring-1 focus:ring-[#141414] rounded p-1 transition-all"
                                placeholder="0.00"
                              />
                              {showActuals && (
                                <div className="text-[10px] font-mono opacity-40 w-full text-right border-l border-[#141414]/10 pl-2">
                                  {actual.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                </div>
                              )}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {isBudgetModalOpen && (
        <div
          className="fixed inset-0 overflow-y-auto overscroll-contain bg-[#141414]/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setIsBudgetModalOpen(false);
          }}
        >
          <div className="bg-[#E4E3E0] border-2 border-[#141414] rounded-3xl shadow-[16px_16px_0px_0px_rgba(20,20,20,1)] w-full max-w-md max-h-[calc(100dvh-2rem)] overflow-y-auto overscroll-contain">
            <div className="px-8 py-6 border-b-2 border-[#141414] flex justify-between items-center">
              <h2 className="text-2xl font-bold italic serif">New Budget</h2>
              <button onClick={() => setIsBudgetModalOpen(false)} className="p-2 hover:bg-[#141414] hover:text-[#E4E3E0] rounded-full transition-all">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-8 space-y-6">
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest opacity-70 mb-2">Budget Name</label>
                <input 
                  type="text"
                  value={newBudgetName}
                  onChange={(e) => setNewBudgetName(e.target.value)}
                  placeholder="e.g. 2026 Growth Plan"
                  className="w-full px-4 py-3 bg-white/50 border-2 border-[#141414] rounded-xl font-bold focus:outline-none focus:ring-4 focus:ring-[#141414]/5 transition-all"
                />
              </div>
              {trackingCategories.map(category => (
                <div key={category.id}>
                  <label className="block text-[10px] font-bold uppercase tracking-widest opacity-70 mb-2">{category.name}</label>
                  <select 
                    value={newBudgetTrackingSelections[category.id] || ''}
                    onChange={(e) => setNewBudgetTrackingSelections(prev => ({
                      ...prev,
                      [category.id]: e.target.value
                    }))}
                    className="w-full px-4 py-3 bg-white/50 border-2 border-[#141414] rounded-xl font-bold focus:outline-none focus:ring-4 focus:ring-[#141414]/5 transition-all"
                  >
                    <option value="">All {category.name}</option>
                    {category.options?.map((option: string) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
              ))}
              <button 
                onClick={handleLocalBudgetCreate}
                disabled={!newBudgetName.trim() || isBudgetSaving}
                className="w-full py-4 bg-[#141414] text-[#E4E3E0] rounded-full font-bold text-xs uppercase tracking-widest hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isBudgetSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                Create Budget
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
